import type {
  ObservabilityApiRequestFact,
  ObservabilityComputeResourceType,
  ObservabilityEcsTaskFact,
  ObservabilityLambdaInvocationFact,
  ObservabilityServiceDetailResponse,
  ObservabilityServiceInventoryItem,
  ObservabilityServiceMetricSummary,
} from '@scatterin/api-types';
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Braces,
  Clock3,
  CloudCog,
  Container,
  DollarSign,
  FunctionSquare,
  Gauge,
  ListTree,
  Search,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { DataPanel } from '@/components/dataPanel';
import { EmptyState, ErrorState, LoadingState, StateView } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Drawer } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { useServiceDetailQuery, useServiceInventoryQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatBytes, formatDuration, formatInteger, formatRelativeTime, formatTime } from '@/lib/format';
import { resourceTypeValues } from '@/lib/urlState';

const resourceLabels: Record<ObservabilityComputeResourceType, string> = {
  api_route: 'API routes',
  ecs_task_definition: 'Fargate task definitions',
  lambda_function: 'Lambda functions',
  sqs_queue: 'SQS queues',
};

const resourceIcons = {
  api_route: Braces,
  ecs_task_definition: Container,
  lambda_function: FunctionSquare,
  sqs_queue: ListTree,
} satisfies Record<ObservabilityComputeResourceType, typeof Activity>;

const formatPercent = (value: number | null | undefined): string => {
  if (typeof value !== 'number') return 'n/a';
  const precision = value < 0.01 ? 2 : 1;
  return `${(value * 100).toFixed(precision)}%`;
};

const formatPercentValue = (value: number | null | undefined): string => (typeof value === 'number' ? `${value.toFixed(1)}%` : 'n/a');

const formatUsd = (value: number | null | undefined): string =>
  (value ?? 0).toLocaleString(undefined, {
    currency: 'USD',
    maximumFractionDigits: value && value < 0.01 ? 6 : 2,
    minimumFractionDigits: 2,
    style: 'currency',
  });

const coverageTone = (coverage: ObservabilityServiceInventoryItem['telemetryCoverage']): 'amber' | 'green' | 'slate' => {
  if (coverage === 'complete') return 'green';
  if (coverage === 'partial') return 'amber';
  return 'slate';
};

const coverageLabel = (item: ObservabilityServiceInventoryItem): string => {
  if (item.telemetryCoverage === 'complete') return 'native count matched';
  if (item.telemetryCoverage === 'partial' && (item.metrics.observations ?? 0) > 0) return 'operation facts present';
  if (item.telemetryCoverage === 'partial') return 'aggregate metrics only';
  return 'no operation facts';
};

const resourceEvidenceTone = (fact: ObservabilityEcsTaskFact): 'amber' | 'green' | 'red' | 'slate' => {
  if (fact.outOfMemory) return 'red';
  if (fact.exitCode === 137) return 'amber';
  return fact.resourceMetricsCoverage === 'complete' ? 'green' : 'slate';
};

const resourceEvidenceLabel = (fact: ObservabilityEcsTaskFact): string => {
  if (fact.outOfMemory) return 'explicit out of memory';
  if (fact.exitCode === 137) return 'exit 137 · cause unverified';
  return fact.resourceMetricsCoverage ?? 'not measured';
};

const requestTone = (statusCode: number): 'amber' | 'green' | 'red' => {
  if (statusCode >= 500) return 'red';
  if (statusCode >= 400) return 'amber';
  return 'green';
};

const operationLabel = (resourceType: ObservabilityComputeResourceType): string => {
  if (resourceType === 'api_route') return 'Requests';
  if (resourceType === 'lambda_function') return 'Invocations';
  if (resourceType === 'ecs_task_definition') return 'Tasks';
  return 'Measurements';
};

const errorLabel = (resourceType: ObservabilityComputeResourceType): string => {
  if (resourceType === 'api_route') return 'Server error rate (5xx)';
  if (resourceType === 'lambda_function') return 'Invocation error rate';
  if (resourceType === 'ecs_task_definition') return 'Task failure rate';
  return 'Error rate';
};

const startupValue = (item: ObservabilityServiceInventoryItem): string => {
  if (item.resourceType === 'lambda_function') {
    return formatDuration(item.metrics.initializationDuration.p95, item.metrics.initializationDuration.unit);
  }
  if (item.resourceType === 'ecs_task_definition') {
    return formatDuration(item.metrics.startupDuration.p95, item.metrics.startupDuration.unit);
  }
  return 'not applicable';
};

const configText = (config: ObservabilityServiceInventoryItem['configuration'], key: string): string | null => {
  const value = config?.[key];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
};

const ResourceSummary = ({ item }: { item: ObservabilityServiceInventoryItem }) => {
  const Icon = resourceIcons[item.resourceType];
  return (
    <div className="grid min-w-0 gap-2">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="bg-selection text-action grid size-9 shrink-0 place-items-center rounded-lg">
            <Icon aria-hidden="true" className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="text-ink truncate font-semibold">{item.displayName}</p>
            <p className="text-muted mt-1 truncate text-xs">{item.serviceName ?? item.resourceKey}</p>
          </div>
        </div>
        <Badge className="shrink-0" tone={coverageTone(item.telemetryCoverage)}>
          {coverageLabel(item)}
        </Badge>
      </div>
      <p className="text-muted text-xs">{item.coverageSummary}</p>
    </div>
  );
};

const MetricCells = ({ item }: { item: ObservabilityServiceInventoryItem }) => (
  <>
    <TableCell className="numeric">{formatInteger(item.metrics.observations)}</TableCell>
    <TableCell className="numeric">{formatPercent(item.metrics.errorRate)}</TableCell>
    <TableCell className="numeric">{formatDuration(item.metrics.duration.p95, item.metrics.duration.unit)}</TableCell>
    <TableCell className="numeric">{startupValue(item)}</TableCell>
    <TableCell className="numeric">{formatUsd(item.metrics.estimatedCostUsd)}</TableCell>
  </>
);

const SummaryCard = ({ icon, label: cardLabel, value }: { icon: ReactNode; label: string; value: string }) => (
  <Card>
    <CardContent>
      <p className="text-muted flex items-center gap-2 text-xs font-semibold">
        {icon}
        {cardLabel}
      </p>
      <p className="text-ink numeric mt-2 text-xl font-semibold">{value}</p>
    </CardContent>
  </Card>
);

const DistributionCards = ({ label, metric }: { label: string; metric: ObservabilityServiceMetricSummary['duration'] }) => (
  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
    <SummaryCard icon={<Activity className="size-4" />} label={`Average ${label}`} value={formatDuration(metric.average, metric.unit)} />
    <SummaryCard icon={<Gauge className="size-4" />} label={`P95 ${label}`} value={formatDuration(metric.p95, metric.unit)} />
    <SummaryCard icon={<Gauge className="size-4" />} label={`P99 ${label}`} value={formatDuration(metric.p99, metric.unit)} />
    <SummaryCard icon={<Clock3 className="size-4" />} label={`Maximum ${label}`} value={formatDuration(metric.maximum, metric.unit)} />
  </div>
);

const QueueHealth = ({ item }: { item: ObservabilityServiceInventoryItem }) => {
  const native = item.nativeMetrics ?? {};
  return (
    <section className="grid gap-3">
      <h3 className="text-ink text-sm font-semibold">Queue health</h3>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard icon={<ListTree className="size-4" />} label="Visible messages" value={formatInteger(native.visibleMessages)} />
        <SummaryCard icon={<Activity className="size-4" />} label="In flight" value={formatInteger(native.inFlightMessages)} />
        <SummaryCard icon={<Clock3 className="size-4" />} label="Delayed" value={formatInteger(native.delayedMessages)} />
        <SummaryCard
          icon={<Clock3 className="size-4" />}
          label="Oldest message"
          value={formatDuration(native.oldestMessageAgeSeconds, 'Seconds')}
        />
      </div>
      <p className="text-muted text-xs">
        <span>
          Latest one-hour AWS totals: {formatInteger(native.messagesSent)} sent · {formatInteger(native.messagesReceived)} received.
        </span>{' '}
        <span>Queue depth is a point-in-time inventory snapshot.</span>
      </p>
    </section>
  );
};

const HistoryStrip = ({ data }: { data: ObservabilityServiceDetailResponse }) => {
  const history = data.history ?? [];
  const maximum = Math.max(1, ...history.map((point) => point.metrics.duration.p95 ?? 0));
  const bucketHours =
    history.length > 0 ? (new Date(history[0].periodEnd).getTime() - new Date(history[0].periodStart).getTime()) / 3_600_000 : 24;
  return (
    <div className="grid gap-3">
      <div className="border-line bg-panel flex min-h-36 items-end gap-1 rounded-lg border p-3" role="img" aria-label="P95 latency history">
        {history.map((point) => {
          const value = point.metrics.duration.p95 ?? 0;
          return (
            <div className="group relative flex min-w-0 flex-1 items-end" key={point.periodStart}>
              <div
                className="bg-action/65 group-hover:bg-action w-full min-w-1 rounded-sm transition-colors"
                style={{ height: `${Math.max(value > 0 ? 8 : 2, (value / maximum) * 100)}%` }}
              />
              <span className="border-line bg-raised text-ink pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 rounded border px-2 py-1 text-xs whitespace-nowrap shadow-sm group-hover:block">
                {new Date(point.periodStart).toLocaleDateString()} · {formatDuration(value, point.metrics.duration.unit)}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-muted text-xs">
        {bucketHours < 24 ? `${bucketHours}-hour` : 'Daily'} P95 response or execution latency. Empty buckets are retained to make telemetry
        gaps visible.
      </p>
    </div>
  );
};

const provenanceTone = (status: string): 'amber' | 'green' | 'slate' => {
  if (status === 'available') return 'green';
  if (status === 'partial') return 'amber';
  return 'slate';
};

const ProvenancePanel = ({ data }: { data: ObservabilityServiceDetailResponse }) => (
  <section className="grid gap-3">
    <div>
      <h3 className="text-ink text-sm font-semibold">Measurement provenance</h3>
      <p className="text-muted mt-1 text-xs">Every signal states its source, exact selected window, sample count, and freshness.</p>
    </div>
    <div className="grid gap-2">
      {(data.metricProvenance ?? []).map((entry) => (
        <div className="border-line grid gap-2 rounded-lg border p-3" key={entry.metricKey}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-ink text-sm font-semibold">{entry.label}</p>
              <p className="text-muted mt-0.5 text-xs">{entry.source}</p>
            </div>
            <Badge tone={provenanceTone(entry.status)}>{entry.status.replace('_', ' ')}</Badge>
          </div>
          <p className="text-muted text-xs">{entry.detail}</p>
          <dl className="grid gap-2 text-xs sm:grid-cols-3">
            <div>
              <dt className="text-muted">Window</dt>
              <dd className="text-ink mt-0.5">
                {formatTime(entry.windowStart)} – {formatTime(entry.windowEnd)}
              </dd>
            </div>
            <div>
              <dt className="text-muted">Samples</dt>
              <dd className="text-ink numeric mt-0.5">{formatInteger(entry.sampleCount)}</dd>
            </div>
            <div>
              <dt className="text-muted">Latest sample</dt>
              <dd className="text-ink mt-0.5" title={formatTime(entry.lastObservedAt)}>
                {entry.lastObservedAt ? formatRelativeTime(entry.lastObservedAt) : 'No sample'}
              </dd>
            </div>
          </dl>
        </div>
      ))}
    </div>
  </section>
);

const ConfigHistory = ({ data }: { data: ObservabilityServiceDetailResponse }) => (
  <section className="grid gap-3">
    <div>
      <h3 className="text-ink text-sm font-semibold">Configuration and deployment history</h3>
      <p className="text-muted mt-1 text-xs">Only changes are shown; unchanged hourly inventory snapshots are collapsed.</p>
    </div>
    {(data.configurationHistory ?? []).length === 0 ? (
      <EmptyState title="Configuration history begins after the immutable snapshot migration" tone="coverage" />
    ) : (
      <div className="grid gap-2">
        {(data.configurationHistory ?? []).map((snapshot) => (
          <div className="border-line rounded-lg border p-3" key={`${snapshot.observedAt}:${snapshot.configurationHash}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-ink text-sm font-semibold" title={snapshot.deploymentId ?? undefined}>
                {snapshot.deploymentId ? `Deployment ${snapshot.deploymentId.slice(0, 12)}` : (snapshot.version ?? 'Configuration change')}
              </p>
              <span className="text-muted text-xs" title={formatTime(snapshot.observedAt)}>
                {formatRelativeTime(snapshot.observedAt)}
              </span>
            </div>
            <p className="text-muted mt-2 text-xs">
              {configText(snapshot.configuration, 'runtime') ?? configText(snapshot.configuration, 'packageType') ?? 'AWS resource'} ·{' '}
              {configText(snapshot.configuration, 'memoryMb') ? `${configText(snapshot.configuration, 'memoryMb')} MB` : 'memory n/a'} ·{' '}
              {configText(snapshot.configuration, 'timeoutSeconds')
                ? `${configText(snapshot.configuration, 'timeoutSeconds')} s timeout`
                : 'timeout n/a'}
            </p>
          </div>
        ))}
      </div>
    )}
  </section>
);

const WorkloadBreakdown = ({ data }: { data: ObservabilityServiceDetailResponse }) => {
  if ((data.workloadBreakdown ?? []).length === 0) return null;
  return (
    <section className="grid gap-3">
      <div>
        <h3 className="text-ink text-sm font-semibold">Performance by workload</h3>
        <p className="text-muted mt-1 text-xs">Latency is segmented by recorded workload class instead of blending unlike jobs.</p>
      </div>
      <TableShell>
        <Table aria-label="Performance by workload" style={{ minWidth: '40rem' }}>
          <TableHeader>
            <TableRow>
              <TableHead>Workload</TableHead>
              <TableHead>Runs</TableHead>
              <TableHead>Average files</TableHead>
              <TableHead>Average input</TableHead>
              <TableHead>P95 end to end</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.workloadBreakdown ?? []).map((workload) => (
              <TableRow key={workload.workloadSizeClass}>
                <TableCell>
                  <Badge tone={workload.workloadSizeClass === 'unknown' ? 'slate' : 'green'}>{workload.workloadSizeClass}</Badge>
                </TableCell>
                <TableCell className="numeric">{formatInteger(workload.observations)}</TableCell>
                <TableCell className="numeric">{formatInteger(workload.inputFileCount.average)}</TableCell>
                <TableCell className="numeric">{formatBytes(workload.inputTotalBytes.average)}</TableCell>
                <TableCell className="numeric">{formatDuration(workload.duration.p95, workload.duration.unit)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>
    </section>
  );
};

const LambdaRuntimeUtilization = ({ metrics }: { metrics: ObservabilityServiceMetricSummary }) => {
  const available =
    (metrics.cpuTotalTime.count ?? 0) > 0 ||
    (metrics.networkTotalBytes.count ?? 0) > 0 ||
    (metrics.temporaryStorageUsedBytes.count ?? 0) > 0 ||
    (metrics.fileDescriptorsUsed.count ?? 0) > 0 ||
    (metrics.threadsUsed.count ?? 0) > 0;
  return (
    <section className="grid gap-3">
      <div>
        <h3 className="text-ink text-sm font-semibold">Deep runtime utilization</h3>
        <p className="text-muted mt-1 text-xs">
          Lambda Insights measurements inside the selected window, summarized from stored samples rather than a latest snapshot.
        </p>
      </div>
      {available ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <SummaryCard
            icon={<Gauge className="size-4" />}
            label="Average CPU time"
            value={formatDuration(metrics.cpuTotalTime.average, metrics.cpuTotalTime.unit)}
          />
          <SummaryCard
            icon={<Activity className="size-4" />}
            label="Average network"
            value={formatBytes(metrics.networkTotalBytes.average)}
          />
          <SummaryCard
            icon={<Gauge className="size-4" />}
            label="Peak temporary storage"
            value={formatBytes(metrics.temporaryStorageUsedBytes.maximum)}
          />
          <SummaryCard
            icon={<Gauge className="size-4" />}
            label="Peak file descriptors"
            value={formatInteger(metrics.fileDescriptorsUsed.maximum)}
          />
          <SummaryCard icon={<Gauge className="size-4" />} label="Peak threads" value={formatInteger(metrics.threadsUsed.maximum)} />
        </div>
      ) : (
        <EmptyState title="Lambda Insights has not produced runtime-utilization samples for this function" tone="coverage" />
      )}
    </section>
  );
};

const LambdaFacts = ({ facts }: { facts: ObservabilityLambdaInvocationFact[] }) => (
  <TableShell>
    <Table aria-label="Recent Lambda invocations" style={{ minWidth: '48rem' }}>
      <TableHeader>
        <TableRow>
          <TableHead>Observed</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Duration / billed</TableHead>
          <TableHead>Memory used / allocated</TableHead>
          <TableHead>Startup</TableHead>
          <TableHead>Version</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {facts.map((fact) => (
          <TableRow key={`${fact.requestId ?? 'unknown'}-${fact.observedAt}`}>
            <TableCell title={formatTime(fact.observedAt)}>{formatRelativeTime(fact.observedAt)}</TableCell>
            <TableCell>
              <Badge tone={fact.status === 'success' ? 'green' : 'red'}>{fact.status}</Badge>
            </TableCell>
            <TableCell className="numeric">
              {formatDuration(fact.durationMs, 'Milliseconds')} / {formatDuration(fact.billedDurationMs, 'Milliseconds')}
            </TableCell>
            <TableCell className="numeric">
              {fact.maxMemoryUsedMb ?? 'n/a'} / {fact.memorySizeMb ?? 'n/a'} MB
            </TableCell>
            <TableCell className="numeric">{fact.likelyColdStart ? formatDuration(fact.initDurationMs, 'Milliseconds') : 'warm'}</TableCell>
            <TableCell>{fact.functionVersion ?? 'unknown'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableShell>
);

const ApiFacts = ({ facts }: { facts: ObservabilityApiRequestFact[] }) => (
  <TableShell>
    <Table aria-label="Recent API requests" style={{ minWidth: '46rem' }}>
      <TableHeader>
        <TableRow>
          <TableHead>Observed</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Response</TableHead>
          <TableHead>Integration</TableHead>
          <TableHead>Gateway overhead</TableHead>
          <TableHead>Payload</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {facts.map((fact) => {
          const statusCode = fact.statusCode ?? 0;
          const overhead =
            typeof fact.responseLatencyMs === 'number' && typeof fact.integrationLatencyMs === 'number'
              ? Math.max(0, fact.responseLatencyMs - fact.integrationLatencyMs)
              : undefined;
          return (
            <TableRow key={fact.requestId}>
              <TableCell title={formatTime(fact.observedAt)}>{formatRelativeTime(fact.observedAt)}</TableCell>
              <TableCell>
                <Badge tone={requestTone(statusCode)}>{statusCode}</Badge>
              </TableCell>
              <TableCell className="numeric">{formatDuration(fact.responseLatencyMs, 'Milliseconds')}</TableCell>
              <TableCell className="numeric">{formatDuration(fact.integrationLatencyMs, 'Milliseconds')}</TableCell>
              <TableCell className="numeric">{formatDuration(overhead, 'Milliseconds')}</TableCell>
              <TableCell className="numeric">{formatBytes(fact.responseLengthBytes)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableShell>
);

const EcsFacts = ({ facts }: { facts: ObservabilityEcsTaskFact[] }) => (
  <TableShell>
    <Table aria-label="Recent Fargate tasks" style={{ minWidth: '52rem' }}>
      <TableHeader>
        <TableRow>
          <TableHead>Stopped</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Startup</TableHead>
          <TableHead>Image pull</TableHead>
          <TableHead>Active</TableHead>
          <TableHead>CPU / memory</TableHead>
          <TableHead>Peak utilization</TableHead>
          <TableHead>Resource evidence</TableHead>
          <TableHead>Image</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {facts.map((fact) => (
          <TableRow key={fact.taskArn}>
            <TableCell title={formatTime(fact.stoppedAt)}>{formatRelativeTime(fact.stoppedAt)}</TableCell>
            <TableCell>
              <Badge tone={fact.status === 'failed' ? 'red' : 'green'}>{fact.status}</Badge>
            </TableCell>
            <TableCell className="numeric">{formatDuration(fact.startupDurationMs, 'Milliseconds')}</TableCell>
            <TableCell className="numeric">{formatDuration(fact.imagePullDurationMs, 'Milliseconds')}</TableCell>
            <TableCell className="numeric">{formatDuration(fact.activeDurationMs, 'Milliseconds')}</TableCell>
            <TableCell className="numeric">
              {fact.vCpu ?? 'n/a'} vCPU / {fact.memoryGb ?? 'n/a'} GB
            </TableCell>
            <TableCell className="numeric">
              {typeof fact.cpuUtilizationPeakPercent === 'number' ? `${fact.cpuUtilizationPeakPercent.toFixed(1)}% CPU` : 'CPU n/a'} /{' '}
              {typeof fact.memoryUtilizationPeakPercent === 'number'
                ? `${fact.memoryUtilizationPeakPercent.toFixed(1)}% memory`
                : 'memory n/a'}
            </TableCell>
            <TableCell>
              <Badge tone={resourceEvidenceTone(fact)}>{resourceEvidenceLabel(fact)}</Badge>
            </TableCell>
            <TableCell className="max-w-48 truncate" title={fact.imageDigest ?? undefined}>
              {fact.imageDigest ?? 'unknown'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableShell>
);

// The detail composes four resource-specific evidence layouts in one drawer.
// eslint-disable-next-line sonarjs/cognitive-complexity
const ServiceDetailContent = ({ currentSearch, data }: { currentSearch: string; data: ObservabilityServiceDetailResponse }) => {
  if (!data.item) return null;
  const item = data.item;
  const metrics = item.metrics;
  const isLambda = item.resourceType === 'lambda_function';
  const isApi = item.resourceType === 'api_route';
  const isEcs = item.resourceType === 'ecs_task_definition';
  const isQueue = item.resourceType === 'sqs_queue';
  const operationFacts = (data.lambdaInvocations?.length ?? 0) + (data.apiRequests?.length ?? 0) + (data.ecsTasks?.length ?? 0);
  const logGroup = data.lambdaInvocations?.[0]?.logGroup;
  return (
    <div className="grid gap-6">
      {!isQueue ? (
        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <SummaryCard
            icon={<Activity className="size-4" />}
            label={operationLabel(item.resourceType)}
            value={formatInteger(metrics.observations)}
          />
          <SummaryCard icon={<Gauge className="size-4" />} label={errorLabel(item.resourceType)} value={formatPercent(metrics.errorRate)} />
          <SummaryCard
            icon={<Clock3 className="size-4" />}
            label="P95 latency"
            value={formatDuration(metrics.duration.p95, metrics.duration.unit)}
          />
          <SummaryCard icon={<DollarSign className="size-4" />} label="Estimated cost" value={formatUsd(metrics.estimatedCostUsd)} />
        </section>
      ) : null}

      {isQueue ? <QueueHealth item={item} /> : null}

      <StateView
        title={data.insights?.[0] ?? 'No derived insight'}
        tone={(data.insights?.[0] ?? '').includes('No material') ? 'healthy' : 'coverage'}
      >
        {(data.insights ?? []).slice(1).join(' ')}
      </StateView>

      {!isQueue ? (
        <section className="grid gap-3">
          <h3 className="text-ink text-sm font-semibold">Latency history</h3>
          <HistoryStrip data={data} />
        </section>
      ) : null}

      {isLambda ? (
        <>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Execution duration</h3>
            <DistributionCards label="execution" metric={metrics.duration} />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Billed duration</h3>
            <DistributionCards label="billed duration" metric={metrics.billedDuration} />
          </section>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              icon={<CloudCog className="size-4" />}
              label="Cold starts"
              value={`${formatInteger(metrics.coldStarts)} · ${formatPercent(metrics.coldStartRate)}`}
            />
            <SummaryCard
              icon={<Clock3 className="size-4" />}
              label="P95 initialization"
              value={formatDuration(metrics.initializationDuration.p95, metrics.initializationDuration.unit)}
            />
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="Average memory used"
              value={`${formatInteger(metrics.memoryUsedMb.average)} MB`}
            />
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="P95 memory utilization"
              value={formatPercentValue(metrics.memoryUtilization.p95)}
            />
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="Maximum memory used"
              value={`${formatInteger(metrics.memoryUsedMb.maximum)} MB`}
            />
            <SummaryCard icon={<Activity className="size-4" />} label="Throttles (latest 1h)" value={formatInteger(metrics.throttles)} />
            <SummaryCard
              icon={<Activity className="size-4" />}
              label="Maximum concurrency (latest 1h)"
              value={formatInteger(metrics.maxConcurrency)}
            />
            <SummaryCard
              icon={<Clock3 className="size-4" />}
              label="Runtime extension overhead"
              value={formatDuration(metrics.runtimeExtensionDuration.average, metrics.runtimeExtensionDuration.unit)}
            />
          </section>
          <section className="grid gap-3 sm:grid-cols-3">
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="Memory headroom at P95"
              value={metrics.memoryHeadroomAtP95Mb == null ? 'Not measured' : `${Math.round(metrics.memoryHeadroomAtP95Mb)} MB`}
            />
            <SummaryCard
              icon={<Clock3 className="size-4" />}
              label="Timeout headroom at P95"
              value={formatDuration(metrics.timeoutHeadroomAtP95Ms, 'Milliseconds')}
            />
            <SummaryCard
              icon={<Clock3 className="size-4" />}
              label="P95 restore"
              value={formatDuration(metrics.restoreDuration.p95, metrics.restoreDuration.unit)}
            />
          </section>
          <LambdaRuntimeUtilization metrics={metrics} />
        </>
      ) : null}

      {isApi ? (
        <>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Response latency</h3>
            <DistributionCards label="response latency" metric={metrics.duration} />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Integration latency</h3>
            <DistributionCards label="integration latency" metric={metrics.integrationDuration} />
          </section>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard icon={<Activity className="size-4" />} label="Client errors (4xx)" value={formatInteger(metrics.clientErrors)} />
            <SummaryCard icon={<Activity className="size-4" />} label="Server errors (5xx)" value={formatInteger(metrics.serverErrors)} />
            <SummaryCard icon={<Gauge className="size-4" />} label="Integration share" value={formatPercent(metrics.usefulComputeShare)} />
            <SummaryCard
              icon={<FunctionSquare className="size-4" />}
              label="Integration Lambda"
              value={configText(item.configuration, 'integrationFunctionName') ?? 'Not resolved'}
            />
          </section>
        </>
      ) : null}

      {isEcs ? (
        <>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Task startup</h3>
            <DistributionCards label="startup" metric={metrics.startupDuration} />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Active task duration</h3>
            <DistributionCards label="active duration" metric={metrics.activeDuration} />
          </section>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="Latest CPU utilization"
              value={formatPercentValue(metrics.cpuUtilization.average)}
            />
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="Latest memory utilization"
              value={formatPercentValue(metrics.memoryUtilization.average)}
            />
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="Latest CPU headroom"
              value={formatPercentValue(metrics.latestAverageCpuHeadroomPercent)}
            />
            <SummaryCard
              icon={<Gauge className="size-4" />}
              label="Useful compute share"
              value={formatPercent(metrics.usefulComputeShare)}
            />
          </section>
        </>
      ) : null}

      <WorkloadBreakdown data={data} />
      <ProvenancePanel data={data} />
      <ConfigHistory data={data} />

      {isLambda || isEcs ? (
        <section className="grid gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-ink text-sm font-semibold">Versions and deployments</h3>
            {logGroup ? (
              <Button asChild variant="secondary">
                <Link
                  to={{
                    pathname: '/explore',
                    search: `${currentSearch}&view=logs&logGroup=${encodeURIComponent(logGroup)}`,
                  }}
                >
                  Contextual logs
                </Link>
              </Button>
            ) : null}
          </div>
          {(data.versions ?? []).length === 0 ? (
            <EmptyState title="No version evidence in this window" tone="coverage" />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {(data.versions ?? []).map((version) => (
                <div
                  className="border-line rounded-lg border p-3"
                  key={`${version.version}:${version.deploymentId ?? ''}:${version.memorySizeMb ?? ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-ink max-w-64 truncate font-semibold" title={version.version}>
                      {version.version}
                      {version.memorySizeMb ? ` · ${version.memorySizeMb} MB` : ''}
                    </p>
                    <Badge tone={(version.errors ?? 0) > 0 ? 'amber' : 'green'}>{formatInteger(version.errors)} errors</Badge>
                  </div>
                  <p className="text-muted numeric mt-2 text-xs">
                    {version.deploymentId ? `deployment ${version.deploymentId.slice(0, 12)} · ` : ''}
                    {formatInteger(version.observations)} operations · P95 {formatDuration(version.duration.p95, version.duration.unit)} ·{' '}
                    {formatUsd(version.estimatedCostUsd)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : null}

      <section className="grid gap-3">
        <h3 className="text-ink text-sm font-semibold">Recent operations</h3>
        {operationFacts === 0 ? <EmptyState title="No operation facts in the selected window" tone="coverage" /> : null}
        {(data.lambdaInvocations?.length ?? 0) > 0 ? <LambdaFacts facts={data.lambdaInvocations ?? []} /> : null}
        {(data.apiRequests?.length ?? 0) > 0 ? <ApiFacts facts={data.apiRequests ?? []} /> : null}
        {(data.ecsTasks?.length ?? 0) > 0 ? <EcsFacts facts={data.ecsTasks ?? []} /> : null}
      </section>
    </div>
  );
};

const ServiceDetailDrawer = ({ close, currentSearch }: { close: () => void; currentSearch: string }) => {
  const { state } = useRouteContext();
  const query = useServiceDetailQuery(state);
  const data = query.data;

  return (
    <Drawer
      description={data?.item?.coverageSummary ?? 'Loading bounded service telemetry.'}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      open={Boolean(state.resourceKey && state.resourceType)}
      side="right"
      size="lg"
      title={data?.item?.displayName ?? 'Service details'}
    >
      {query.isLoading ? <LoadingState label="Loading service operations" /> : null}
      {query.isError ? <ErrorState message={query.error.message} onRetry={() => void query.refetch()} /> : null}
      {data ? <ServiceDetailContent currentSearch={currentSearch} data={data} /> : null}
    </Drawer>
  );
};

export const ServicesPage = () => {
  const { setState, state } = useRouteContext();
  const query = useServiceInventoryQuery(state);
  const items = query.data?.items ?? [];
  const complete = items.filter((item) => item.telemetryCoverage === 'complete').length;
  const observed = items.filter((item) => item.telemetryCoverage !== 'missing').length;
  const currentSearch = new URLSearchParams({ end: state.end.toISOString(), env: state.env, start: state.start.toISOString() }).toString();
  const selectResource = (item: ObservabilityServiceInventoryItem) =>
    setState({ resourceKey: item.resourceKey, resourceType: item.resourceType });

  return (
    <div className="grid min-w-0 gap-5">
      <StateView
        title={query.data ? `${formatInteger(query.data.total)} compute and request surfaces discovered` : 'Discovering compute surfaces'}
        tone={query.data && complete === items.length && items.length > 0 ? 'healthy' : 'coverage'}
      >
        {query.data
          ? `${formatInteger(observed)} visible resources have operation facts in this window. Inventory and operation coverage are shown separately so an existing resource is never mistaken for observed traffic.`
          : `Loading the bounded ${state.env} inventory and operation evidence.`}
      </StateView>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {resourceTypeValues.map((resourceType) => {
          const Icon = resourceIcons[resourceType];
          const count = query.data?.byResourceType?.find((bucket) => bucket.key === resourceType)?.count ?? 0;
          const active = state.resourceTypes.includes(resourceType);
          return (
            <button
              aria-pressed={active}
              className={`border-line bg-panel hover:border-line-strong rounded-lg border p-4 text-left transition-colors ${active ? 'ring-action ring-2' : ''}`}
              key={resourceType}
              onClick={() =>
                setState({
                  resourceOffset: 0,
                  resourceTypes: active
                    ? state.resourceTypes.filter((value) => value !== resourceType)
                    : [...state.resourceTypes, resourceType],
                })
              }
              type="button"
            >
              <span className="text-muted flex items-center gap-2 text-xs font-semibold">
                <Icon className="size-4" />
                {resourceLabels[resourceType]}
              </span>
              <span className="text-ink numeric mt-2 block text-2xl font-semibold">{formatInteger(count)}</span>
            </button>
          );
        })}
      </section>

      <Card>
        <CardHeader className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle>Services and resources</CardTitle>
            <p className="text-muted mt-1 text-xs">Search by function, API route, task family, queue, or AWS resource key.</p>
          </div>
          <label className="text-muted grid min-w-60 gap-1.5 text-xs font-semibold">
            Find a resource
            <span className="relative">
              <Search aria-hidden="true" className="text-muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                className="pl-9"
                onChange={(event) => setState({ resourceOffset: 0, search: event.target.value })}
                placeholder="Search services"
                value={state.search}
              />
            </span>
          </label>
        </CardHeader>
        <CardContent>
          <DataPanel
            empty={!query.isLoading && !query.isError && items.length === 0}
            emptyTitle={
              state.search || state.resourceTypes.length > 0
                ? 'No resources match these filters'
                : 'No compute resources have been inventoried'
            }
            emptyTone={state.search || state.resourceTypes.length > 0 ? 'filtered' : 'coverage'}
            error={query.error}
            isError={query.isError}
            isLoading={query.isLoading}
            loadingLabel={`Loading ${state.env} compute inventory`}
            onRetry={() => void query.refetch()}
          >
            <div className="grid gap-3 md:hidden">
              {items.map((item) => (
                <Card key={`${item.resourceType}:${item.resourceKey}`}>
                  <CardContent className="grid gap-4">
                    <ResourceSummary item={item} />
                    <dl className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-muted text-xs">Operations</dt>
                        <dd className="numeric font-semibold">{formatInteger(item.metrics.observations)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted text-xs">Error rate</dt>
                        <dd className="numeric font-semibold">{formatPercent(item.metrics.errorRate)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted text-xs">P95 latency</dt>
                        <dd className="numeric font-semibold">{formatDuration(item.metrics.duration.p95, item.metrics.duration.unit)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted text-xs">Estimate</dt>
                        <dd className="numeric font-semibold">{formatUsd(item.metrics.estimatedCostUsd)}</dd>
                      </div>
                    </dl>
                    <Button onClick={() => selectResource(item)} variant="secondary">
                      Inspect details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <TableShell className="hidden md:block">
              <Table aria-label="Service inventory" style={{ minWidth: '72rem' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Resource / coverage</TableHead>
                    <TableHead>Operations</TableHead>
                    <TableHead>Error rate</TableHead>
                    <TableHead>P95 latency</TableHead>
                    <TableHead>P95 startup</TableHead>
                    <TableHead>Estimate</TableHead>
                    <TableHead>Last inventory</TableHead>
                    <TableHead className="text-right">Inspect</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={`${item.resourceType}:${item.resourceKey}`}>
                      <TableCell>
                        <ResourceSummary item={item} />
                      </TableCell>
                      <MetricCells item={item} />
                      <TableCell title={formatTime(item.lastObservedAt)}>{formatRelativeTime(item.lastObservedAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button onClick={() => selectResource(item)} variant="ghost">
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          </DataPanel>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <Button
          disabled={state.resourceOffset === 0}
          onClick={() => setState({ resourceOffset: Math.max(0, state.resourceOffset - state.resourcePageSize) })}
          variant="secondary"
        >
          <ArrowLeft className="size-4" />
          Previous
        </Button>
        <span className="text-muted numeric text-xs">
          {items.length === 0 ? 0 : state.resourceOffset + 1}–{state.resourceOffset + items.length} of {formatInteger(query.data?.total)}
        </span>
        <Button
          disabled={!query.data?.hasMore}
          onClick={() => setState({ resourceOffset: state.resourceOffset + state.resourcePageSize })}
          variant="secondary"
        >
          Next
          <ArrowRight className="size-4" />
        </Button>
      </div>

      <ServiceDetailDrawer close={() => setState({ resourceKey: null, resourceType: null })} currentSearch={currentSearch} />
    </div>
  );
};
